import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import Input from '../common/Input/Input';
import CheckBox from '../common/CheckBox/CheckBox';
import { Button } from '../ui/Button/Button';
import S from './DrivingHoursTrackerStyles';
import { DHTLimitsResponse } from '../../API/types/Drivers';
import useGetDHTLimitsPerDriver from '../../API/hooks/drivers/useGetDHTLimitsPerDriver';
import useGetDHTLimitsPerFleet from '../../API/hooks/drivers/useGetDHTLimitsPerFleet';
import usePostDHTLimits from '../../API/hooks/drivers/usePostDHTLimits';
import usePatchDHTLimits from '../../API/hooks/drivers/usePatchDHTLimits';
import { DriverSettingsDefault } from '../../constants/DriverSettings';
import { dthValidation } from '../../pages/DeviceDetails/Modals/DeviceSettings/helpers';

type Props = {
  fleetId: string;
  driverId?: string;
  toggleModal: (closed: boolean) => void;
  readonly?: boolean;
};

type FormValues = {
  limitTripInSec: string;
  limitDayInSec: string;
  limitWeekInSec: string;
  limitMonthInSec: string;
  lockDriverDhtEdit: boolean;
};

function DrivingHoursTracker({
  fleetId,
  driverId,
  toggleModal,
  readonly,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);
  const { data: fleetDhtLimits, isLoading: fleetLoading, error: fleetError } = useGetDHTLimitsPerFleet(fleetId);
  const {
    data: driverDhtLimits,
    isLoading: driverLoading,
    error: driverError,
  } = useGetDHTLimitsPerDriver(fleetId, driverId || '', !!driverId);
  const dhtLimits = driverId ? driverDhtLimits : fleetDhtLimits;
  const isLoading = driverId ? driverLoading : fleetLoading;
  const error = driverId ? driverError : fleetError;
  const { mutate: postData } = usePostDHTLimits(onSubmitSuccess, onSubmitFail);
  const { mutate: patchData } = usePatchDHTLimits(onSubmitSuccess, onSubmitFail);
  const noConfig = error && ((error as any).response?.status === 404);
  const isDriverDhtLocked = !!driverId && !!fleetDhtLimits?.lockDriverDhtEdit;
  const isReadOnly = !!readonly || isDriverDhtLocked;

  const DefaultValues: FormValues = {
    limitTripInSec: DriverSettingsDefault.dhtLimits.limitTripInSec.toString(),
    limitDayInSec: DriverSettingsDefault.dhtLimits.limitDayInSec.toString(),
    limitWeekInSec: DriverSettingsDefault.dhtLimits.limitWeekInSec.toString(),
    limitMonthInSec: DriverSettingsDefault.dhtLimits.limitMonthInSec.toString(),
    lockDriverDhtEdit: false,
  };

  const formik = useFormik<FormValues>({
    initialValues: DefaultValues,
    onSubmit: (values) => {
      const payload = {
        fleetId,
        driverId,
        limitTripInSec: parseInt(values.limitTripInSec, 10) * 3600,
        limitDayInSec: parseInt(values.limitDayInSec, 10) * 3600,
        limitWeekInSec: parseInt(values.limitWeekInSec, 10) * 3600,
        limitMonthInSec: parseInt(values.limitMonthInSec, 10) * 3600,
        ...(driverId ? {} : { lockDriverDhtEdit: values.lockDriverDhtEdit }),
      } as unknown as DHTLimitsResponse;

      if (noConfig) {
        postData(payload);
        return;
      }
      patchData(payload);
    },
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!dthValidation.test(values.limitTripInSec)) {
        errors.limitTripInSec = 'Can contain only positive numbers';
      }
      if (!dthValidation.test(values.limitDayInSec)) {
        errors.limitDayInSec = 'Can contain only positive numbers';
      }
      if (!dthValidation.test(values.limitWeekInSec)) {
        errors.limitWeekInSec = 'Can contain only positive numbers';
      }
      if (!dthValidation.test(values.limitMonthInSec)) {
        errors.limitMonthInSec = 'Can contain only positive numbers';
      }
      return errors;
    },
    validateOnMount: true,
  });

  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleLockChange = (_name: string, checked: boolean): void => {
    formik.setFieldValue('lockDriverDhtEdit', checked);
    setSubmitError('');
  };

  useEffect(() => {
    formik.setValues({
      limitTripInSec: ((dhtLimits?.limitTripInSec !== undefined) && (typeof dhtLimits?.limitTripInSec === 'number')) ? (dhtLimits.limitTripInSec / 3600).toString() : DriverSettingsDefault.dhtLimits.limitTripInSec.toString(),
      limitDayInSec: ((dhtLimits?.limitDayInSec !== undefined) && (typeof dhtLimits?.limitDayInSec === 'number')) ? (dhtLimits.limitDayInSec / 3600).toString() : DriverSettingsDefault.dhtLimits.limitDayInSec.toString(),
      limitWeekInSec: ((dhtLimits?.limitWeekInSec !== undefined) && (typeof dhtLimits?.limitWeekInSec === 'number')) ? (dhtLimits.limitWeekInSec / 3600).toString() : DriverSettingsDefault.dhtLimits.limitWeekInSec.toString(),
      limitMonthInSec: ((dhtLimits?.limitMonthInSec !== undefined) && (typeof dhtLimits?.limitMonthInSec === 'number')) ? (dhtLimits.limitMonthInSec / 3600).toString() : DriverSettingsDefault.dhtLimits.limitMonthInSec.toString(),
      lockDriverDhtEdit: driverId
        ? false
        : (fleetDhtLimits?.lockDriverDhtEdit ?? false),
    }, false);
  }, [dhtLimits, driverId, fleetDhtLimits?.lockDriverDhtEdit]);

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <p className="form-title">Set limits (hours) for generating of driver&apos;s overtime alert</p>
          </S.RowContainer>
          <S.RowContainer className={(noConfig && driverId && !isReadOnly) ? '' : 'hidden'}>
            <p className="message warning">Note: No config for this driver. Please set new parameters if needed.</p>
          </S.RowContainer>
          <S.RowContainer className={(noConfig && !driverId && !isReadOnly) ? '' : 'hidden'}>
            <p className="message warning">Note: No config for fleet. Please set new parameters if needed.</p>
          </S.RowContainer>
          <S.RowContainer className={isDriverDhtLocked ? '' : 'hidden'}>
            <p className="message warning">Driver DHT limits are locked by fleet settings.</p>
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Limit per trip (h)"
              type="text"
              key="dhtLimitsTrip"
              name="limitTripInSec"
              placeholder="Type value more then 0"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              defaultValue={DefaultValues.limitTripInSec}
              value={formik.values.limitTripInSec}
              disabled={isLoading || isReadOnly}
              error={formik.touched.limitTripInSec && formik.errors.limitTripInSec}
              maxLength={4}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Limit per day (h)"
              type="text"
              key="dhtLimitsDay"
              name="limitDayInSec"
              placeholder="Type value more then 0"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              defaultValue={DefaultValues.limitDayInSec}
              value={formik.values.limitDayInSec}
              disabled={isLoading || isReadOnly}
              error={formik.touched.limitDayInSec && formik.errors.limitDayInSec}
              maxLength={4}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Limit per week (h)"
              type="text"
              key="dhtLimitsWeek"
              name="limitWeekInSec"
              placeholder="Type value more then 0"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              defaultValue={DefaultValues.limitWeekInSec}
              value={formik.values.limitWeekInSec}
              disabled={isLoading || isReadOnly}
              error={formik.touched.limitWeekInSec && formik.errors.limitWeekInSec}
              maxLength={4}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Limit per month (h)"
              type="text"
              key="dhtLimitsMonth"
              name="limitMonthInSec"
              placeholder="Type value more then 0"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              defaultValue={DefaultValues.limitMonthInSec}
              value={formik.values.limitMonthInSec}
              disabled={isLoading || isReadOnly}
              error={formik.touched.limitMonthInSec && formik.errors.limitMonthInSec}
              maxLength={4}
            />
          </S.RowContainer>
          {!driverId && !readonly && (
            <S.RowContainer>
              <CheckBox
                label="Lock driver DHT editing on Driver Details page"
                name="lockDriverDhtEdit"
                state={formik.values.lockDriverDhtEdit}
                onChange={handleLockChange}
              />
            </S.RowContainer>
          )}
          <S.RowContainer>
            <div className="actions">
              <Button
                height="50px"
                onClick={() => toggleModal(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="add-button"
                height="50px"
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !!submitError || isLoading || isReadOnly}
              >
                Save
              </Button>
            </div>
          </S.RowContainer>

          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((e) => <S.ErrorMessage key={e}>{e}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default DrivingHoursTracker;
